<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
        {{ resolvedTitle }}
      </h1>
      <p class="text-gray-600 dark:text-gray-300">
        {{ resolvedDescription }}
      </p>
    </div>

    <div
      class="bg-white dark:bg-surface-800"
      :class="{ 'rounded-lg shadow ': !dialogRef }"
    >
      <div class="p-6">
        <Form
          v-slot="$form"
          :validate-on="validateOn"
          :resolver="createUserSchema"
          :initial-values="initialValues"
          class="space-y-6"
          @submit="onSubmit"
        >
          <!-- Role Selection -->
          <FormField
            v-if="!resolvedHideRoleField"
            v-slot="{ field, errorMessage }"
            name="roleIds"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Rôle(s) <span class="text-red-500">*</span>
            </label>
            <MultiSelect
              v-bind="field"
              :options="availableRoles"
              option-label="label"
              option-value="value"
              :placeholder="resolvedRolePlaceholder"
              class="w-full"
              :filter="true"
              :invalid="!!errorMessage"
              :disabled="resolvedLockRoles"
            >
              <template #option="{ option }">
                <div class="font-medium">
                  {{ option.label }}
                </div>
              </template>
            </MultiSelect>
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>
          <FormField
            v-if="resolvedRelatedSelectionField"
            v-slot="{ field, errorMessage }"
            :name="resolvedRelatedSelectionField"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              {{ resolvedRelatedSelectionLabel }}
              <span
                v-if="resolvedRelatedSelectionRequired"
                class="text-red-500"
              >*</span>
            </label>
            <MultiSelect
              v-bind="field"
              :options="resolvedRelatedSelectionOptions"
              option-label="label"
              option-value="value"
              :placeholder="resolvedRelatedSelectionPlaceholder"
              class="w-full"
              :filter="true"
              :invalid="!!errorMessage"
              display="chip"
            >
              <template #option="{ option }">
                <div>
                  <div class="font-medium">
                    {{ option.label }}
                  </div>
                  <div
                    v-if="option.city_name"
                    class="text-sm text-gray-500"
                  >
                    {{ option.city_name }}
                  </div>
                </div>
              </template>
            </MultiSelect>
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{ errorMessage }}</small>
          </FormField>
          <FormField
            v-if="!resolvedHideCityAssignments && $form?.roleIds?.value?.includes(4)"
            v-slot="{ field, errorMessage }"
            v-access="{
              permissions: [Permissions.UsersCreate, Permissions.UsersUpdate],
            }"
            name="cityIds"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Villes assignées
            </label>
            <MultiSelect
              v-bind="field"
              :options="cities"
              option-label="label"
              option-value="value"
              placeholder="Sélectionner des villes"
              class="w-full"
              :filter="true"
              :invalid="!!errorMessage"
            >
              <template #option="{ option }">
                <div class="flex gap-2 items-center">
                  <div
                    class="w-3 h-3 rounded-full"
                    :style="{ backgroundColor: option.color || '#ccc' }"
                  />
                  <div>
                    <div class="font-medium">
                      {{ option.label }}
                    </div>
                    <div class="text-sm text-gray-500">
                      {{ option.postcode }}
                    </div>
                  </div>
                </div>
              </template>
            </MultiSelect>
            <small class="text-gray-500">Les villes assignées déterminent l'accès de l'utilisateur</small>
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>
          <!-- Civility -->
          <FormField
            v-slot="{ field, errorMessage }"
            name="civility"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Civilité <span class="text-red-500">*</span>
            </label>
            <Select
              v-bind="field"
              :options="civilityOptions"
              option-label="label"
              option-value="value"
              placeholder="Sélectionner une civilité"
              class="w-full"
              :invalid="!!errorMessage"
            />
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>

          <!-- Name -->
          <FormField
            v-slot="{ field, errorMessage }"
            name="name"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Nom <span class="text-red-500">*</span>
            </label>
            <InputText
              v-bind="field"
              placeholder="Nom complet"
              class="w-full"
              :invalid="!!errorMessage"
            />
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>

          <!-- Email -->
          <FormField
            v-slot="{ field, errorMessage }"
            name="email"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Email <span class="text-red-500">*</span>
            </label>
            <InputText
              v-bind="field"
              type="email"
              placeholder="Email"
              class="w-full"
              :invalid="!!errorMessage"
            />
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>

          <!-- Phone -->
          <FormField
            v-slot="{ field, errorMessage }"
            name="phone"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Téléphone <span class="text-red-500">*</span>
            </label>
            <InputText
              v-bind="field"
              placeholder="Numéro de téléphone"
              class="w-full"
              :invalid="!!errorMessage"
            />
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>

          <!-- Password -->
          <FormField
            v-slot="{ field, errorMessage }"
            name="password"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Mot de passe <span class="text-red-500">*</span>
            </label>
            <Password
              v-bind="field"
              placeholder="Mot de passe (min. 8 caractères)"
              :feedback="false"
              toggle-mask
              :invalid="!!errorMessage"
            />
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>

          <!-- Password Confirmation -->
          <FormField
            v-slot="{ field, errorMessage }"
            name="password_confirmation"
          >
            <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Confirmer le mot de passe <span class="text-red-500">*</span>
            </label>
            <Password
              v-bind="field"
              placeholder="Confirmer le mot de passe"
              :feedback="false"
              toggle-mask
              :invalid="!!errorMessage"
            />
            <small
              v-if="errorMessage"
              class="text-red-500"
            >{{
              errorMessage
            }}</small>
          </FormField>

          <!-- Action Buttons -->
          <div class="flex gap-3 justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              label="Annuler"
              variant="outlined"
              type="button"
              @click="handleCancel"
            />
            <Button
              :label="resolvedSubmitLabel"
              type="submit"
              :loading="saving"
              :disabled="!canCreateUser"
            />
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Form, FormField } from "@primevue/forms";
import { z } from "zod";
import MultiSelect from "primevue/multiselect";
import Password from "primevue/password";
import Permissions from "~/types/Permissions";
import Role from "~/types/Role";
import { canCreateUserWithRoles } from "~/utils/canEditUserTarget";
import { canAssignUserRole } from "~/utils/canAssignUserRole";
import type { RoleWithPermissions } from "~/types/Role";
import { zodResolver } from "@primevue/forms/resolvers/zod";

type CreatedUser = Record<string, unknown>;
type CivilityOption = { name: string };
type CityOption = { id: number; name: string; color?: string | null };
type FormSubmitEvent = {
  valid: boolean;
  values: Record<string, unknown> & { roleIds?: number[] };
};
type ApiError = { data?: { message?: string } };

const emit = defineEmits<{
  cancel: [];
  userCreated: [user: CreatedUser];
}>();

const props = withDefaults(defineProps<{
  allowedRoleNames?: Role[];
  defaultRoleIds?: number[];
  lockRoles?: boolean;
  hideRoleField?: boolean;
  hideCityAssignments?: boolean;
  relatedSelectionField?: string;
  relatedSelectionRequired?: boolean;
  relatedSelectionLabel?: string;
  relatedSelectionPlaceholder?: string;
  relatedSelectionOptions?: Array<{ label: string; value: number; city_name?: string | null }>;
  title?: string;
  description?: string;
  submitLabel?: string;
  rolePlaceholder?: string;
  onAfterCreate?: (user: CreatedUser, selectedValues: number[]) => Promise<void> | void;
}>(), {
  allowedRoleNames: undefined,
  defaultRoleIds: undefined,
  lockRoles: undefined,
  hideRoleField: false,
  hideCityAssignments: false,
  relatedSelectionField: undefined,
  relatedSelectionRequired: false,
  relatedSelectionLabel: undefined,
  relatedSelectionPlaceholder: undefined,
  relatedSelectionOptions: undefined,
  title: undefined,
  description: undefined,
  submitLabel: undefined,
  rolePlaceholder: undefined,
  onAfterCreate: undefined,
});

// For dynamic dialog
const dialogRef = inject("dialogRef");

const client = useSanctumClient();
const { $hasPermission } = useNuxtApp();

// Reactive state
const saving = ref(false);
const validateOn = "submit";

const userRoles = useRoles();
const userStore = useUserStore();
await userStore.getUser();
const dialogData = computed(() => dialogRef?.value?.data ?? {});

const { data: rolesData } = await client("/api/roles");
const { data: citiesData } = await client("/api/cities?no_pagination=true");
const { data: civilities } = await client("/api/civilities/search", {
  method: "POST",
  body: {
    filters: [
      {
        field: "id",
        operator: "in",
        value: [1, 2, 3],
      },
    ],
  },
});

const resolvedAllowedRoleNames = computed<Role[] | undefined>(
  () => props.allowedRoleNames ?? dialogData.value.allowedRoleNames,
);
const resolvedLockRoles = computed<boolean>(
  () => props.lockRoles ?? dialogData.value.lockRoles ?? false,
);
const resolvedHideRoleField = computed<boolean>(
  () => props.hideRoleField ?? dialogData.value.hideRoleField ?? false,
);
const resolvedHideCityAssignments = computed<boolean>(
  () => props.hideCityAssignments ?? dialogData.value.hideCityAssignments ?? false,
);
const resolvedRelatedSelectionField = computed<string | undefined>(
  () => props.relatedSelectionField ?? dialogData.value.relatedSelectionField,
);
const resolvedRelatedSelectionRequired = computed<boolean>(
  () => props.relatedSelectionRequired ?? dialogData.value.relatedSelectionRequired ?? false,
);
const resolvedRelatedSelectionLabel = computed<string>(
  () => props.relatedSelectionLabel ?? dialogData.value.relatedSelectionLabel ?? "Sélection",
);
const resolvedRelatedSelectionPlaceholder = computed<string>(
  () => props.relatedSelectionPlaceholder ?? dialogData.value.relatedSelectionPlaceholder ?? "Sélectionnez une ou plusieurs options",
);
const resolvedRelatedSelectionOptions = computed<Array<{ label: string; value: number; city_name?: string | null }>>(
  () => props.relatedSelectionOptions ?? dialogData.value.relatedSelectionOptions ?? [],
);
const resolvedTitle = computed<string>(
  () => props.title ?? dialogData.value.title ?? "Créer un utilisateur",
);
const resolvedDescription = computed<string>(
  () => props.description ?? dialogData.value.description ?? "Créez un nouvel utilisateur et assignez-lui des rôles",
);
const resolvedSubmitLabel = computed<string>(
  () => props.submitLabel ?? dialogData.value.submitLabel ?? "Créer l'utilisateur",
);
const resolvedRolePlaceholder = computed<string>(
  () => props.rolePlaceholder ?? dialogData.value.rolePlaceholder ?? "Sélectionner des rôles",
);

const filteredRoles = computed(() => {
  const roles = (rolesData ?? []).filter((role: RoleWithPermissions) =>
    canAssignUserRole(role.name, userRoles.value)
    || (resolvedLockRoles.value && resolvedAllowedRoleNames.value?.length === 1
      && resolvedAllowedRoleNames.value[0] === Role.AUDITOR && role.name === Role.AUDITOR),
  );
  if (!resolvedAllowedRoleNames.value?.length) {
    return roles;
  }

  return roles.filter((role: RoleWithPermissions) =>
    resolvedAllowedRoleNames.value?.includes(role.name as Role),
  );
});

const civilityOptions = computed(() => {
  const civilitiesOptions = [{ label: "Non défini", value: "ND" }];
  civilitiesOptions.push(
    ...((civilities ?? []) as CivilityOption[]).map((civility) => {
      return {
        label: civility.name,
        value: civility.name,
      };
    }),
  );
  return civilitiesOptions;
});

const availableRoles = computed(() => {
  return filteredRoles.value
    ?.filter((role: RoleWithPermissions) => role.name !== Role.MERCHANT)
    ?.map((role: RoleWithPermissions) => {
      return {
        label: role.name,
        value: role.id,
      };
    });
});

const cities = computed(
  () =>
    ((citiesData ?? []) as CityOption[]).map((city) => {
      return {
        label: city.name,
        value: city.id,
        color: city.color,
      };
    }) ?? [],
);

const resolvedDefaultRoleIds = computed<number[]>(() => {
  const explicitDefaultRoleIds = props.defaultRoleIds ?? dialogData.value.defaultRoleIds;
  if (Array.isArray(explicitDefaultRoleIds) && explicitDefaultRoleIds.length > 0) {
    return explicitDefaultRoleIds;
  }

  if (!resolvedAllowedRoleNames.value?.length) {
    return [];
  }

  return filteredRoles.value
    .filter((role: RoleWithPermissions) => resolvedAllowedRoleNames.value?.includes(role.name as Role))
    .map((role: RoleWithPermissions) => role.id);
});

const canCreateUser = computed(() => canCreateUserWithRoles(resolvedLockRoles.value, $hasPermission));

const initialValues = computed(() => ({
  roleIds: resolvedDefaultRoleIds.value,
  ...(resolvedRelatedSelectionField.value ? { [resolvedRelatedSelectionField.value]: [] } : {}),
}));

// Zod validation schema
const createUserSchema = zodResolver(
  z
    .object({
      roleIds: z
        .array(z.number())
        .min(1, "Au moins un rôle doit être sélectionné"),
      civility: z.string().min(1, "La civilité est requise"),
      name: z.string().min(3, "Le nom doit contenir au moins 3 caractères"),
      email: z.string().email("Format d'email invalide"),
      phone: z
        .string()
        .min(3, "Le téléphone doit contenir au moins 3 caractères"),
      password: z
        .string()
        .min(8, "Le mot de passe doit contenir au moins 8 caractères"),
      password_confirmation: z.string(),
      cityIds: z.array(z.number()).optional(),
      [resolvedRelatedSelectionField.value ?? "__unused_related_selection__"]: z.array(z.number()).optional(),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: "Les mots de passe ne correspondent pas",
      path: ["passwordConfirmation"],
    })
    .superRefine((data, ctx) => {
      const fieldName = resolvedRelatedSelectionField.value;
      if (!fieldName || !resolvedRelatedSelectionRequired.value) {
        return;
      }

      const selectedValues = (data as Record<string, unknown>)[fieldName];
      if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Au moins une option doit être sélectionnée",
          path: [fieldName],
        });
      }
    }),
);

// Log only whitelisted fields: a FetchError holds options.body, which contains the password.
const safeErrorDetails = (error: unknown) => {
  const { name, message, stack, statusCode, status } = (error ?? {}) as Record<string, unknown>;
  return { name, message, statusCode: statusCode ?? status, stack };
};

// Submit form
const onSubmit = async (values: FormSubmitEvent) => {
  if (values.valid && canCreateUser.value) {
    const { $eventBus } = useNuxtApp();
    let newUser: CreatedUser;

    try {
      saving.value = true;

      const payload = {
        ...values.values,
        roleIds: resolvedLockRoles.value || resolvedHideRoleField.value
          ? resolvedDefaultRoleIds.value
          : values.values.roleIds,
      };

      if (!Array.isArray(payload.roleIds) || payload.roleIds.length === 0
        || payload.roleIds.some(roleId => !filteredRoles.value.some(role => role.id === roleId))) {
        saving.value = false;
        return;
      }

      newUser = await client("/api/users", {
        method: "POST",
        body: payload,
      });
    }
    catch (error) {
      saving.value = false;
      if (error instanceof z.ZodError) {
        // Zod validation errors are handled by the form
        return;
      }

      console.error("[Create.vue] échec de la création", safeErrorDetails(error));
      $eventBus.emit("toast", {
        summary: (error as ApiError).data?.message
          || "Erreur lors de la création de l'utilisateur. Réessayez ; si le problème continue, contactez le support.",
        severity: "error",
      });
      return;
    }

    try {
      const afterCreate = props.onAfterCreate ?? dialogData.value.onAfterCreate;
      const relatedSelectionField = resolvedRelatedSelectionField.value;
      const selectedValues = relatedSelectionField && Array.isArray(values.values[relatedSelectionField])
        ? values.values[relatedSelectionField].filter((value): value is number => typeof value === "number")
        : [];

      if (afterCreate) {
        await afterCreate(newUser, selectedValues);
      }

      // Show success message
      $eventBus.emit("toast", {
        summary: "Utilisateur créé avec succès",
        severity: "success",
      });

      emit("userCreated", newUser);

      // Close the dialog if opened via dynamic dialog
      if (dialogRef) {
        dialogRef.value.close({ userCreated: newUser });
      }
    }
    catch (error) {
      console.error("[Create.vue] l'utilisateur a été créé, échec après création", safeErrorDetails(error));
      $eventBus.emit("toast", {
        summary: "Utilisateur créé, mais une erreur est survenue ensuite. Rechargez la page.",
        severity: "error",
      });
    }
    finally {
      saving.value = false;
    }
  }
};

// Handle cancel action
const handleCancel = () => {
  emit("cancel");

  // Close the dialog if opened via dynamic dialog
  if (dialogRef) {
    dialogRef.value.close();
  }
};
</script>
